import { useState } from 'react'
import { Badge, Button } from '@mantine/core'
import { makeUserSuperAdmin, removeUserFromSuperAdmin, setUserReportViewer } from './helper'

interface IProps {
  userId: string
  name?: string
  isSuperAdmin: boolean
  isReportViewer: boolean
  onChanged: () => void
}

// Grant/revoke super-admin and reports-only access straight from a user's
// detail page (the Admin Users page offers the same via search).
const AdminAccessCard: React.FC<IProps> = ({ userId, name, isSuperAdmin, isReportViewer, onChanged }) => {
  const [busy, setBusy] = useState(false)

  const run = (question: string, action: () => Promise<any>) => {
    if (!window.confirm(question)) return
    setBusy(true)
    action()
      .then(() => onChanged())
      .catch((err) => alert(err.response?.data?.message || 'Could not update access'))
      .finally(() => setBusy(false))
  }

  const who = name || 'this user'

  return (
    <div className="bg-white rounded-xl shadow-md p-6 mt-6">
      <p className="text-xl font-bold">Admin access</p>
      <p className="text-gray-500 text-sm mb-4">
        Super admins see everything here. Reports access only opens the Reports page (including CSV export).
      </p>
      <div className="flex flex-col gap-3">
        <div className="flex flex-wrap items-center gap-3">
          <p className="font-bold w-40">Super admin</p>
          <Badge color={isSuperAdmin ? 'green' : 'gray'}>{isSuperAdmin ? 'Yes' : 'No'}</Badge>
          {isSuperAdmin ? (
            <Button
              size="xs"
              color="red"
              disabled={busy}
              onClick={() => run(`Remove super admin access from ${who}?`, () => removeUserFromSuperAdmin(userId))}
            >
              Remove super admin
            </Button>
          ) : (
            <Button
              size="xs"
              disabled={busy}
              onClick={() =>
                run(`Make ${who} a super admin? They will see and manage everything here.`, () =>
                  makeUserSuperAdmin(userId)
                )
              }
            >
              Make super admin
            </Button>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <p className="font-bold w-40">Reports access</p>
          <Badge color={isReportViewer ? 'green' : 'gray'}>{isReportViewer ? 'Yes' : 'No'}</Badge>
          {isReportViewer ? (
            <Button
              size="xs"
              color="red"
              disabled={busy}
              onClick={() => run(`Remove reports access from ${who}?`, () => setUserReportViewer(userId, false))}
            >
              Remove reports access
            </Button>
          ) : (
            <Button
              size="xs"
              variant="outline"
              disabled={busy}
              onClick={() =>
                run(`Give ${who} reports-only access? They will be able to view and export the Reports page.`, () =>
                  setUserReportViewer(userId, true)
                )
              }
            >
              Give reports access
            </Button>
          )}
        </div>
      </div>
    </div>
  )
}

export default AdminAccessCard
